import { Injectable } from '@angular/core';
import { Media, Playback } from '../playback';
import { Logger, Shell } from '@tytapp/common';
import { ApiAvAsset } from '@tytapp/api';

export interface Playable {
    id?: number | string;
    title?: string;
    description?: string;
    readonly asset: ApiAvAsset;
    adapt(data: any);
}

export interface Playlist extends Playable {
    playables: Playable[];
}

export interface QueueItem {
    parent?: QueueItem;
    id: string;
    media: Media;
    playable?: Playable;
    finished: boolean;

    /**
     * Whether the player should resume this item from the position given in `position`
     */
    resume: boolean;

    /**
     * True if the queue item was added "incidentally", that means that the
     * user didn't explicitly ask to be shown this later.
     */
    incidental: boolean;

    /**
     * The position the user reached while playing this content
     */
    position?: number;

    /**
     * Subitems which come from Playlist-type playables such as multi-part episodes.
     */
    subitems: QueueItem[];
}

/**
 * The player service which controls the primary player UI
 */
@Injectable()
export class PlayerService {
    constructor(
        private shell: Shell,
        private playback: Playback
    ) {

    }

    private _queue: QueueItem[] = [];
    private _currentQueueItem: QueueItem = null;
    private _media: Media;

    isSupported(queueItem: QueueItem) {
        if (queueItem.playable && queueItem.playable['playables']) {
            return true;
        }

        // Resolve the media, if necessary.

        let resolution = queueItem.media.resolution;

        if (!resolution) {
            queueItem.media.resolution
                = resolution
                = this.playback.resolve(<any>queueItem.media); // TODO
        }

        // If no resolution was found, then the URL is not supported. This is either a bug
        // or a human error.

        return !!resolution;
    }

    removeQueueItem(item: QueueItem) {
        if (item.parent) {
            item.parent.subitems = item.parent.subitems.filter(x => x !== item);
        } else {
            this._queue = this._queue.filter(x => x !== item);
        }
    }

    /**
     * The position which the player should start at when playing new media.
     */
    public resumePosition: number;

    /**
     * Instruct the player to stop playing the current media and close itself.
     */
    public stopPlaying() {
        if (this._currentQueueItem) {
            this.removeQueueItem(this._currentQueueItem);
            this._currentQueueItem = null;
        }

    }

    /**
     * Generate a new queue ID (uuid v4)
     */
    private generateQueueId() {
        var result, i, j;
        result = '';
        for (j = 0; j < 32; j++) {
            if (j == 8 || j == 12 || j == 16 || j == 20)
                result = result + '-';
            i = Math.floor(Math.random() * 16).toString(16).toUpperCase();
            result = result + i;
        }
        return result;
    }

    private createQueueItem(mediaOrUrl: string | Playable, incidental: boolean, parent: QueueItem = null): QueueItem {
        if (typeof mediaOrUrl === 'object') {
            let playable: Playable = mediaOrUrl;
            let subitems = [];

            // Create the item

            let item = {
                parent,
                id: this.generateQueueId(),
                media: new Media(playable.asset.url),
                playable,
                resume: false,
                subitems,
                incidental,
                finished: false
            };

            // If this playable is a playlist, we need to resolve the items into the queue graph.

            if (playable['playables']) {
                let playlist: Playlist = <Playlist>playable;
                item.subitems = playlist.playables.map(x => this.createQueueItem(x, incidental, item));
            }

            return item;

        } else if (typeof mediaOrUrl === 'string') {

            let media = new Media(mediaOrUrl);

            return {
                id: this.generateQueueId(),
                media,
                playable: null,
                resume: false,
                parent: null,
                subitems: [],
                incidental,
                finished: false
            };

        }

        // This value is not playable

        Logger.inspect('Invalid media passed to PlayerService.play()', mediaOrUrl);
        throw new Error('Cannot play back object of type ' + (typeof mediaOrUrl));
    }

    public updatePlaybackPosition(position: number) {
        if (!this._currentQueueItem)
            return;

        this._currentQueueItem.position = position;
    }

    public clearQueue() {
        this._queue = [];

        // The currently playing media will no longer have any relation to other graph items
        if (this._currentQueueItem)
            this._currentQueueItem.parent = null;
    }

    get queue(): QueueItem[] {
        return this._queue.slice(0);
    }

    get currentItem(): QueueItem {
        return this._currentQueueItem;
    }
}